import React from 'react';
import {View, Text} from 'react-native';
import {ProductModifierType} from 'src/types/Menu';
import Styles from '../../Styles';
import {checkValue} from 'src/Util';
import Localized from 'src/constants/AppStrings';
import {getDescriber} from './descriptor/DescriptorType';
import CustomTextInput from 'src/components/elements/CustomTextInput';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';

const {
  radioButtonProductModifier,
  productModifierStyles,
  checkboxProductModifiers,
  getCounterText,
  requiredModifiersView,
  getModifierPrice,
  customisedCalorieSection,
} = getDescriber();

type ProductModifierProps = WithGlobalizeProps & {
  productModifier: ProductModifierType;
  selectionChanged: (modifierId: string, selections: Array<string>) => void;
  selectedValues?: Array<string>;
  showCalories?: boolean;
  showAsKiloCalories?: boolean;
  sectionCalories?: [];
};
type ProductModifierState = {
  values: Array<string>;
  radio: boolean;
  required: boolean;
  freeText: boolean;
  showUnitName: string;
};
const FREE_TEXT_MAX_LENGTH = 50;
export const ModifierTypes = {
  FreeText: 4,
  Upsell: 5,
};
// Emoji regex pattern
const emojiRegex = /([^\x00-\x7F]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g;

class ProductModifier extends React.Component<
  ProductModifierProps,
  ProductModifierState
> {
  constructor(props: ProductModifierProps) {
    super(props);
    const radio =
      props.productModifier.maxsel === 1 &&
      props.productModifier.type !== ModifierTypes.Upsell;
    let values = props.productModifier.vals
      .filter((value) => value.isdefault === 'Y')
      .map((value) => value.id);

    if (this.props.selectedValues) {
      values = this.props.selectedValues;
    }
    if (!values) {
      values = [];
    }

    this.state = {
      values,
      radio,
      required: props.productModifier.forced === 'Y',
      freeText: props.productModifier.type === ModifierTypes.FreeText,
      showUnitName: props.showAsKiloCalories
        ? Localized.Labels.kcal
        : Localized.Labels.cal_not_all_caps,
    };

    if (values.length > 0) {
      props.selectionChanged(props.productModifier.id, values);
    }
    this.onPress = this.onPress.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  onPress(value: string) {
    let values: Array<string> = [];

    if (this.state.radio) {
      if (!this.state.values.includes(value)) {
        values = [value];
      }
    } else {
      if (this.state.values.includes(value)) {
        values = this.state.values.filter((item) => item !== value);
      } else if (
        this.state.values.length < this.props.productModifier.maxsel ||
        this.props.productModifier.type === ModifierTypes.Upsell
      ) {
        values = [...this.state.values, value];
      } else {
        // values haven't changed
        values = [...this.state.values];
      }
    }

    this.setState({values}, () =>
      this.props.selectionChanged(
        this.props.productModifier.id,
        this.state.values,
      ),
    );
  }

  renderCheckBoxes() {
    return this.props.productModifier.vals.map((value) => {
      let price = '';
      if (value.price !== null) {
        price = getModifierPrice(value?.price, this.props);
      }
      let calories = '';
      if (checkValue(value.calories)) {
        calories = value.calories.toString() + ' calories';
      }
      return checkboxProductModifiers(
        value,
        price,
        calories,
        this.state.values,
        this.state.showUnitName,
        this.props.showCalories,
        this.onPress,
      );
    });
  }

  renderRadioButtons() {
    const buttons = this.props.productModifier.vals.map((value) => {
      let price = '';
      if (value.price !== null) {
        price = getModifierPrice(value?.price, this.props);
      }
      return {
        label: value.name,
        price,
        value: value.id,
        calories: value?.calories,
      };
    });
    return radioButtonProductModifier(
      buttons,
      this.state.values,
      this.props.showCalories,
      this.state.showUnitName,
      this.onPress,
    );
  }

  onChange(value: string) {
    const filteredText = value.replace(emojiRegex, '');
    const values = [filteredText];
    this.setState(
      {
        values,
      },
      () =>
        this.props.selectionChanged(
          this.props.productModifier.id,
          this.state.values,
        ),
    );
  }

  renderFreeTextInput() {
    return (
      <CustomTextInput
        accessible={true}
        label={this.props.productModifier.name}
        accessibilityLabel={this.props.productModifier.name}
        aria-label={this.props.productModifier.name}
        value={this.state.values?.[0]}
        showHelpText={true}
        helpText={getCounterText(
          this.state.values?.[0],
          FREE_TEXT_MAX_LENGTH,
        ).toString()}
        multiline={true}
        numberOfLines={3}
        onChangeText={this.onChange}
        maxLength={FREE_TEXT_MAX_LENGTH}
        helperStyle={productModifierStyles().helperText}
        containerStyle={productModifierStyles().freeTextContainer}
        contentStyle={productModifierStyles().freeTextContent}
        testID="qa_FreeText_fld"
      />
    );
  }

  render() {
    const {
      productModifier: {name, type, maxsel},
    } = this.props;

    const {sectionCalories, showCalories, productModifier} = this.props;

    const {required, freeText} = this.state;
    let content: React.ReactNode = this.renderCheckBoxes();
    const isShowCustomModifier = productModifier.vals.filter(
      (item) => item?.calories !== null && item?.calories !== undefined,
    );
    if (type === ModifierTypes.FreeText) {
      content = this.renderFreeTextInput();
    } else if (maxsel === 1 && type !== ModifierTypes.Upsell) {
      content = this.renderRadioButtons();
    }

    return (
      <View style={productModifierStyles().productModifierContainer}>
        {!freeText && (
          <View style={productModifierStyles().titleRow}>
            {required ? (
              requiredModifiersView(name)
            ) : (
              <View style={productModifierStyles().requiredViewStyles}>
                <Text
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                  accessible={true}
                  accessibilityLabel={`${name} section`}
                  aria-label={`${name} section`}
                  nativeID={`${name} section`}
                  style={productModifierStyles().titleText}
                >
                  {name}
                </Text>
              </View>
            )}
          </View>
        )}
        <View style={productModifierStyles().options}>{content}</View>
        {!freeText &&
          showCalories &&
          isShowCustomModifier?.length > 0 &&
          customisedCalorieSection(
            sectionCalories ?? [],
            productModifier.id,
            this.state.showUnitName,
          )}
      </View>
    );
  }
}
export default withGlobalize(ProductModifier);
